import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Link2, Copy, RefreshCw, Ban, ExternalLink, Layers } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const portalUrl = (token) => `${window.location.origin}/portal/${token}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });

const ConfirmAction = ({ testid, title, description, confirmLabel, onConfirm, children, className }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button data-testid={testid} size="sm" variant="ghost" title={title} className={className}>{children}</Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm}>{confirmLabel}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const FilingSettings = ({ link, entities, onChanged }) => {
  const patch = async (body, msg) => {
    try { await api.patch(`/admin/portal/links/${link.id}`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(formatErr(e)); }
  };
  const extra = link.extra_entities || [];
  const choices = entities.filter((e) => e !== link.client_name);
  const toggle = (e) => patch({ extra_entities: extra.includes(e) ? extra.filter((x) => x !== e) : [...extra, e] }, "Entities updated");
  return (
    <div className="col-span-12 flex flex-wrap items-center gap-4 text-xs text-slate-600 -mt-1">
      <label className="flex items-center gap-2">
        <Switch data-testid={`portal-link-filings-${link.id}`} checked={link.show_filings !== false}
          onCheckedChange={(v) => patch({ show_filings: v }, v ? "Filings shown on portal" : "Filings hidden from portal")} />
        Show filings
      </label>
      {link.show_filings !== false && (
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost" data-testid={`portal-link-entities-${link.id}`} className="h-7 gap-1 text-xs">
              <Layers className="w-3.5 h-3.5" />Also include entities{extra.length ? ` (${extra.length})` : ""}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" data-testid={`portal-link-entities-popover-${link.id}`}>
            <div className="text-xs text-slate-500 mb-2">Filings for “{link.client_name}” are always shown. Tick other entities of this client:</div>
            {choices.length === 0 && <div className="text-xs text-slate-400">No other entities with compliance reminders.</div>}
            <div className="max-h-56 overflow-y-auto space-y-1">
              {choices.map((e) => (
                <label key={e} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                  <Checkbox data-testid={`portal-entity-${e}`} checked={extra.includes(e)} onCheckedChange={() => toggle(e)} />{e}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
      {link.show_filings !== false && extra.length > 0 && <span className="text-slate-500 truncate">+ {extra.join(", ")}</span>}
    </div>
  );
};

const LinkRow = ({ link, entities, onChanged }) => {
  const copy = async () => {
    await navigator.clipboard?.writeText(portalUrl(link.token)).catch(() => {});
    toast.success("Portal link copied");
  };
  const regenerate = async () => {
    try { await api.post(`/admin/portal/links/${link.id}/regenerate`); toast.success("New link generated"); onChanged(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const disable = async () => {
    try { await api.delete(`/admin/portal/links/${link.id}`); toast.success("Link turned off"); onChanged(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <li data-testid={`portal-link-row-${link.id}`} className="px-5 py-3 grid grid-cols-12 gap-3 items-center">
      <div className="col-span-3 font-medium truncate">{link.client_name}</div>
      <div className="col-span-4 text-xs mono text-slate-500 truncate" data-testid={`portal-link-url-${link.id}`}>{portalUrl(link.token)}</div>
      <div className="col-span-2 text-xs">
        {link.is_expired
          ? <span className="px-2 py-0.5 rounded-sm border bg-rose-50 text-rose-700 border-rose-200 uppercase tracking-wider text-[10px]">Expired</span>
          : <span className="text-slate-500">Expires {fmtDate(link.expires_at)}</span>}
      </div>
      <div className="col-span-3 flex justify-end gap-1.5">
        <Button data-testid={`portal-link-copy-${link.id}`} size="sm" variant="outline" onClick={copy} className="gap-1"><Copy className="w-3.5 h-3.5" />Copy</Button>
        <a href={portalUrl(link.token)} target="_blank" rel="noreferrer">
          <Button data-testid={`portal-link-open-${link.id}`} size="sm" variant="ghost"><ExternalLink className="w-3.5 h-3.5" /></Button>
        </a>
        <ConfirmAction testid={`portal-link-regenerate-${link.id}`} title="Replace link" confirmLabel="Replace"
          description={`A new link will be created for ${link.client_name}. The old link will stop working.`} onConfirm={regenerate}>
          <RefreshCw className="w-3.5 h-3.5" />
        </ConfirmAction>
        <ConfirmAction testid={`portal-link-disable-${link.id}`} title="Turn off link" confirmLabel="Turn off" className="text-rose-600"
          description={`${link.client_name} will no longer be able to open their portal.`} onConfirm={disable}>
          <Ban className="w-3.5 h-3.5" />
        </ConfirmAction>
      </div>
      <FilingSettings link={link} entities={entities} onChanged={onChanged} />
    </li>
  );
};

export default function ClientPortalLinks() {
  const [clients, setClients] = useState([]);
  const [links, setLinks] = useState([]);
  const [entities, setEntities] = useState([]);
  const [selected, setSelected] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [c, l, e] = await Promise.all([api.get("/admin/portal/clients"), api.get("/admin/portal/links"), api.get("/admin/portal/entities")]);
      setClients(c.data); setLinks(l.data); setEntities(e.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!selected) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/portal/links", { client_name: selected });
      await navigator.clipboard?.writeText(portalUrl(data.token)).catch(() => {});
      toast.success("Link created & copied");
      setSelected(""); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCreating(false); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Admin</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Client Portal</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Share a private, read-only link so a client can follow the progress of tasks tagged with their name and the status of their statutory filings.
          Links work for 90 days and can be replaced or turned off any time.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6 flex flex-wrap items-end gap-3">
        <div className="w-72">
          <div className="text-xs text-slate-500 mb-1">Client</div>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="portal-client-select"><SelectValue placeholder="Choose a client…" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c} value={c} data-testid={`portal-client-option-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="portal-create-link-btn" onClick={create} disabled={!selected || creating} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
          <Link2 className="w-4 h-4" /> {links.some((l) => l.client_name === selected) ? "Replace link" : "Create link"}
        </Button>
        {clients.length === 0 && <div className="text-xs text-slate-500" data-testid="portal-no-clients">No clients yet — add a client name on a task or a compliance calendar.</div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold">Active links <span className="text-slate-400 font-normal">({links.length})</span></div>
        {links.length === 0
          ? <div className="p-6 text-center text-sm text-slate-500" data-testid="portal-links-empty">No portal links yet.</div>
          : <ul className="divide-y divide-slate-200">{links.map((l) => <LinkRow key={l.id} link={l} entities={entities} onChanged={load} />)}</ul>}
      </div>
    </div>
  );
}
